import React, { useState, useEffect } from 'react';
import { X, Sparkles, MessageCircle, User, Link2 } from 'lucide-react';
import apiClient from "../api/client";


const WingmanModal = ({ open, onClose, token }) => {
  const [activeTab, setActiveTab] = useState('chat');
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Chat Help State
  const [chatMode, setChatMode] = useState('opener');
  const [chatTone, setChatTone] = useState('chill');
  const [conversationSnippet, setConversationSnippet] = useState('');
  const [draft, setDraft] = useState('');
  const [chatSuggestion, setChatSuggestion] = useState('');
  
  // Profile Review State
  const [profileText, setProfileText] = useState('');
  const [bio, setBio] = useState('');
  const [analysis, setAnalysis] = useState('');
  
  // LoveLink Script State
  const [channel, setChannel] = useState('sms');
  const [loveLinkTone, setLoveLinkTone] = useState('chill');
  const [loveLinkUrl, setLoveLinkUrl] = useState('');
  const [script, setScript] = useState('');

  useEffect(() => {
    if (open) {
      fetchUsage();
    }
  }, [open]);

  const fetchUsage = async () => {
    try {
      const res = await apiClient.get(`/wingman/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(res.data);
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  const handleChatHelp = async () => {
    if (!conversationSnippet.trim()) {
      alert('Please provide a conversation snippet');
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post(
        `/wingman/chat`,
        {
          mode: chatMode,
          conversation_snippet: conversationSnippet,
          tone: chatTone,
          draft: chatMode === 'rewrite' ? draft : undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChatSuggestion(res.data.suggestion);
      fetchUsage(); // Refresh usage
    } catch (err) {
      if (err.response?.status === 402) {
        // Payment required - show upgrade message
        const upgradeMsg = err.response.data.detail || 
          "You've reached your free limit. Upgrade to AI Wingman Pro or Elite for more assists!";
        alert(upgradeMsg);
      } else if (err.response?.status === 429) {
        alert(err.response.data.detail || 'Too many requests. Please try again in an hour.');
      } else if (err.response?.status === 500) {
        alert('AI assistant is temporarily unavailable. Please try again in a moment.');
      } else if (!err.response) {
        alert('Network error — check your connection and try again.');
      } else {
        alert(err.response?.data?.detail || 'Something went wrong. Please try again.');
      }
      fetchUsage(); // Refresh to show current status
    } finally {
      setLoading(false);
    }
  };

  const handleProfileReview = async () => {
    if (!profileText.trim() || !bio.trim()) {
      alert('Please provide both profile text and bio');
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post(
        `/wingman/profile-review`,
        { profile_text: profileText, bio },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnalysis(res.data.analysis);
      fetchUsage();
    } catch (err) {
      if (err.response?.status === 402) {
        // Payment required - show upgrade message
        const upgradeMsg = err.response.data.detail || 
          "You've reached your free limit. Upgrade to AI Wingman Pro or Elite for more assists!";
        alert(upgradeMsg);
      } else if (err.response?.status === 429) {
        alert(err.response.data.detail || 'Too many requests. Please try again in an hour.');
      } else if (err.response?.status === 500) {
        alert('AI assistant is temporarily unavailable. Please try again in a moment.');
      } else if (!err.response) {
        alert('Network error — check your connection and try again.');
      } else {
        alert(err.response?.data?.detail || 'Something went wrong. Please try again.');
      }
      fetchUsage(); // Refresh to show current status
    } finally {
      setLoading(false);
    }
  };

  const handleLoveLinkScript = async () => {
    if (!loveLinkUrl.trim()) {
      alert('Please provide your LoveLink URL');
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post(
        `/wingman/lovelink-script`,
        { channel, tone: loveLinkTone, lovelink_url: loveLinkUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScript(res.data.script);
      fetchUsage();
    } catch (err) {
      if (err.response?.status === 402) {
        // Payment required - show upgrade message
        const upgradeMsg = err.response.data.detail || 
          "You've reached your free limit. Upgrade to AI Wingman Pro or Elite for more assists!";
        alert(upgradeMsg);
      } else if (err.response?.status === 429) {
        alert(err.response.data.detail || 'Too many requests. Please try again in an hour.');
      } else if (err.response?.status === 500) {
        alert('AI assistant is temporarily unavailable. Please try again in a moment.');
      } else if (!err.response) {
        alert('Network error — check your connection and try again.');
      } else {
        alert(err.response?.data?.detail || 'Something went wrong. Please try again.');
      }
      fetchUsage(); // Refresh to show current status
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const shareOrCopy = async (text) => {
    // Use native share on mobile if available
    if (navigator.share) {
      try {
        await navigator.share({ text });
        return;
      } catch (err) {
        // User cancelled or share failed — fall through to clipboard
        if (err.name === 'AbortError') return;
      }
    }
    // Fallback to clipboard
    copyToClipboard(text);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-rose-400" />
            <h2 className="text-lg font-bold text-white">Unity Wingman</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Usage Stats */}
        {usage && (
          <div className="px-4 py-3 bg-slate-800/50 border-b border-slate-700">
            <div className="text-xs">
              {usage.has_unlimited ? (
                <div>
                  {usage.is_elite && usage.has_wingman_pro ? (
                    <span className="text-emerald-400 font-semibold">✨ Elite + AI Wingman Pro: Extended assists</span>
                  ) : usage.is_elite ? (
                    <span className="text-emerald-400 font-semibold">✨ Elite: Extended assists</span>
                  ) : usage.has_wingman_pro ? (
                    <span className="text-rose-400 font-semibold">🧠 AI Wingman Pro: Extended assists</span>
                  ) : null}
                </div>
              ) : (
                <div>
                  <div className="text-gray-400 mb-2">
                    Used <strong className="text-white">{usage.used_this_week}</strong> of{' '}
                    <strong className="text-white">{usage.weekly_limit}</strong> free assists this week
                    {usage.remaining > 0 && (
                      <span className="text-emerald-400 ml-1">({usage.remaining} remaining)</span>
                    )}
                  </div>
                  {usage.remaining === 0 && (
                    <div className="mt-2 p-2 bg-rose-900/20 border border-rose-500/30 rounded-lg">
                      <p className="text-rose-300 text-xs font-semibold mb-1">
                        You've used all your free Wingman assists this week!
                      </p>
                      <p className="text-gray-400 text-xs">
                        Upgrade to <strong className="text-white">AI Wingman Pro</strong> or{' '}
                        <strong className="text-white">Elite</strong> for extended access.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 ${
              activeTab === 'chat'
                ? 'text-rose-400 border-b-2 border-rose-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <MessageCircle className="w-4 h-4" />
            Chat Help
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 ${
              activeTab === 'profile'
                ? 'text-rose-400 border-b-2 border-rose-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <User className="w-4 h-4" />
            Profile Review
          </button>
          <button
            onClick={() => setActiveTab('lovelink')}
            className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 ${
              activeTab === 'lovelink'
                ? 'text-rose-400 border-b-2 border-rose-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Link2 className="w-4 h-4" />
            LoveLink Coach
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Chat Help Tab */}
          {activeTab === 'chat' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Mode</label>
                <select
                  value={chatMode}
                  onChange={(e) => setChatMode(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="opener">Opener</option>
                  <option value="reply">Reply</option>
                  <option value="rewrite">Rewrite</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tone</label>
                <select
                  value={chatTone}
                  onChange={(e) => setChatTone(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="chill">Chill</option>
                  <option value="confident">Confident</option>
                  <option value="romantic">Romantic</option>
                  <option value="funny">Funny</option>
                  <option value="respectful">Respectful</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Conversation Snippet
                </label>
                <textarea
                  value={conversationSnippet}
                  onChange={(e) => setConversationSnippet(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                  rows="3"
                  placeholder="Paste the recent conversation here..."
                />
              </div>

              {chatMode === 'rewrite' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Your Draft Message
                  </label>
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                    rows="2"
                    placeholder="What do you want to say?"
                  />
                </div>
              )}

              <button
                onClick={handleChatHelp}
                disabled={loading}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
              >
                {loading ? 'Thinking...' : 'Get Suggestion'}
              </button>

              {chatSuggestion && (
                <div className="mt-4 bg-slate-800 rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-400 mb-2">Suggested Message</div>
                  <p className="text-white text-sm">{chatSuggestion}</p>
                  <button
                    onClick={() => copyToClipboard(chatSuggestion)}
                    className="mt-3 text-xs font-semibold text-rose-400 hover:text-rose-300"
                  >
                    Copy to Clipboard
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Profile Review Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Profile Text (About Section)
                </label>
                <textarea
                  value={profileText}
                  onChange={(e) => setProfileText(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                  rows="3"
                  placeholder="Paste your profile text here..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                  rows="2"
                  placeholder="Your bio..."
                />
              </div>

              <button
                onClick={handleProfileReview}
                disabled={loading}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
              >
                {loading ? 'Analyzing...' : 'Review My Profile'}
              </button>

              {analysis && (
                <div className="mt-4 bg-slate-800 rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-400 mb-2">Wingman's Analysis</div>
                  <p className="text-white text-sm whitespace-pre-wrap">{analysis}</p>
                </div>
              )}
            </div>
          )}

          {/* LoveLink Coach Tab */}
          {activeTab === 'lovelink' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Channel</label>
                <select
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="sms">SMS / Text</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="messenger">Messenger</option>
                  <option value="social">Social Post</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tone</label>
                <select
                  value={loveLinkTone}
                  onChange={(e) => setLoveLinkTone(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="chill">Chill</option>
                  <option value="hype">Hype</option>
                  <option value="romantic">Romantic</option>
                  <option value="business">Business</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Your LoveLink URL
                </label>
                <input
                  type="text"
                  value={loveLinkUrl}
                  onChange={(e) => setLoveLinkUrl(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="https://unitylove.app/ref/YOUR_CODE"
                />
              </div>

              <button
                onClick={handleLoveLinkScript}
                disabled={loading}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
              >
                {loading ? 'Crafting...' : 'Generate Invite Script'}
              </button>

              {script && (
                <div className="mt-4 bg-slate-800 rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-400 mb-2">Your Invite Message</div>
                  <p className="text-white text-sm break-words">{script}</p>
                  <div className="mt-3 flex gap-3">
                    <button
                      onClick={() => shareOrCopy(script)}
                      className="text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 px-3 py-1.5 rounded-lg transition-colors"
                      data-testid="lovelink-share-btn"
                    >
                      {navigator.share ? 'Share' : 'Copy Message'}
                    </button>
                    <button
                      onClick={() => copyToClipboard(script)}
                      className="text-xs font-semibold text-rose-400 hover:text-rose-300"
                      data-testid="lovelink-copy-btn"
                    >
                      Copy to Clipboard
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WingmanModal;
